import { useEffect, useRef, useState } from 'react';

import { animationsDisabled } from 'js/src/libs/css-helpers';

const easeOutCubic = (progress: number) => 1 - Math.pow(1 - progress, 3);

/**
 * Returns `target`, but when it changes from one number to another the returned value counts
 * between them over `durationMs`, so a changed number visibly moves instead of swapping.
 * `null` means there's no value yet (e.g. still loading): the first real value appears as is
 * rather than counting up from nothing. Changes are instant when animations are disabled
 * (reduced motion, tests).
 */
export default function useCountUp(target: number | null, durationMs = 640): number | null {
    const [displayed, setDisplayed] = useState(target);
    const displayedRef = useRef(target);

    useEffect(() => {
        const from = displayedRef.current;
        if (from === null || target === null || from === target || animationsDisabled()) {
            displayedRef.current = target;
            setDisplayed(target);
            return;
        }
        const startedAt = performance.now();
        let frame = 0;
        const step = (now: number) => {
            const progress = Math.min((now - startedAt) / durationMs, 1);
            const value = from + (target - from) * easeOutCubic(progress);
            displayedRef.current = value;
            setDisplayed(value);
            if (progress < 1) {
                frame = requestAnimationFrame(step);
            }
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [target, durationMs]);

    return displayed;
}
